/**
 * CablePathEditHandles
 *
 * Renders draggable handles for editing cable path geometry.
 * Shows handles at:
 * - Start junction (endpoint) - blue, larger
 * - End junction (endpoint) - blue, larger
 * - Intermediate vertices (turning points) - green, smaller
 */

import React from 'react';
import { logger } from '@/lib/logger';
import type { CablePath } from '@/types/cablePaths';
import type { Point } from '@/types/common';

interface CablePathEditHandlesProps {
  path: CablePath;
  junctionCoordMap: Map<string, { x: number; y: number }>;
  zoom: number;
  onDragStart: (
    pathId: string,
    handleType: 'start' | 'end' | 'vertex',
    index: number,
    initialPos: Point,
    e: React.MouseEvent,
  ) => void;
  /** Whether to show handles (only when path is selected and not in public view) */
  isVisible: boolean;
  /** Whether a drag operation is in progress for this path */
  isDragging?: boolean;
  /** Current drag position during drag */
  dragPreviewPos?: Point | null;
  /** Which handle is being dragged */
  draggingHandle?: { type: 'start' | 'end' | 'vertex'; index: number } | null;
}

export function CablePathEditHandles({
  path,
  junctionCoordMap,
  zoom,
  onDragStart,
  isVisible,
  isDragging = false,
  dragPreviewPos = null,
  draggingHandle = null,
}: CablePathEditHandlesProps) {
  // Debug: log what's being rendered
  logger.log('[CablePathEditHandles] Rendering', {
    pathId: path.id,
    isVisible,
    pointsCount: path.points?.length || 0,
    points: path.points,
    hasStartJunction: !!path.start_junction_id,
    hasEndJunction: !!path.end_junction_id,
  });

  if (!isVisible) return null;

  // Get junction coordinates
  const startJunctionCoords = path.start_junction_id
    ? junctionCoordMap.get(path.start_junction_id)
    : null;
  const endJunctionCoords = path.end_junction_id
    ? junctionCoordMap.get(path.end_junction_id)
    : null;

  // Handle sizes (scaled by zoom)
  const endpointRadius = 8 / zoom;
  const vertexRadius = 6 / zoom;
  const strokeWidth = 2 / zoom;

  // Get the display position for a handle (uses drag preview if dragging)
  const getHandlePos = (
    type: 'start' | 'end' | 'vertex',
    index: number,
    defaultPos: Point | null,
  ): Point | null => {
    if (!defaultPos) return null;

    if (
      isDragging &&
      dragPreviewPos &&
      draggingHandle?.type === type &&
      draggingHandle?.index === index
    ) {
      return dragPreviewPos;
    }

    return defaultPos;
  };

  // Get start handle position
  const startPos = getHandlePos(
    'start',
    0,
    startJunctionCoords ? { x: startJunctionCoords.x, y: startJunctionCoords.y } : null,
  );

  // Get end handle position
  const endPos = getHandlePos(
    'end',
    0,
    endJunctionCoords ? { x: endJunctionCoords.x, y: endJunctionCoords.y } : null,
  );

  return (
    <g
      className="cable-path-edit-handles"
      data-path-handles={path.id}
      style={{ pointerEvents: 'auto' }}
    >
      {/* Only intermediate vertex handles - endpoints are shown by junction boxes */}
      {path.points.map((point, index) => {
        const vertexPos = getHandlePos('vertex', index, point);
        if (!vertexPos) return null;

        return (
          <circle
            key={`vertex-${index}`}
            cx={vertexPos.x}
            cy={vertexPos.y}
            r={vertexRadius}
            fill="#22c55e"
            stroke="white"
            strokeWidth={strokeWidth}
            style={{ cursor: 'move', pointerEvents: 'auto' }}
            onMouseDown={e => {
              logger.log('[CablePathEditHandles] Vertex mousedown', {
                pathId: path.id,
                index,
                vertexPos,
              });
              e.preventDefault();
              e.stopPropagation();
              onDragStart(path.id, 'vertex', index, vertexPos, e);
            }}
            data-handle-type="vertex"
            data-handle-index={index}
          />
        );
      })}
    </g>
  );
}
